import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { HiShoppingCart, HiDocumentText, HiCircleStack, HiOutlineShoppingCart } from 'react-icons/hi2';
import { CartService } from '../../service/manage/exam_subscription/cart.service';
import { StudentService } from '../../service/manage/registration/student.service';
import { routes } from '../../helper/routes';
import swal from 'sweetalert';
import PaymentGateSolutions from '../manage/subscription/img/Payment Gateway Solutions.jpg';
import './BuyCartSummary.css';

const BuyCartSummary = () => {
    const history = useHistory();
    const location = useLocation();

    const [assessmentItems, setAssessmentItems] = useState([]);
    const [creditPackageItems, setCreditPackageItems] = useState([]);
    const [subtotal, setSubtotal] = useState(0);
    const [totalDiscount, setTotalDiscount] = useState(0);
    const [total, setTotal] = useState(0);
    const [currencySymbol, setCurrencySymbol] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [creatorUserId, setCreatorUserId] = useState('');
    const [creatorUserTableId, setCreatorUserTableId] = useState('');
    const [billingAddress, setBillingAddress] = useState(null);
    const [billingAddressId, setBillingAddressId] = useState(null);

    useEffect(() => {
        const userType = sessionStorage.getItem('userType');
        if (userType === 'student') {
            const lg_user_id = sessionStorage.getItem('manage_user_id');
            const lg_user_table_id = sessionStorage.getItem('table_log_id');
            StudentService.getCreateUserDetail(lg_user_id, lg_user_table_id, userType)
                .then((response) => {
                    const detail = response.data.user_detail[0];
                    setCreatorUserId(detail.create_user_id);
                    setCreatorUserTableId(detail.create_user_table_id);
                })
                .catch((err) => {
                    console.error('Failed to fetch create user detail:', err);
                });
        }
    }, []);

    useEffect(() => {
        const cartItems = location.state?.cart_items;
        const billingAddressId = location.state?.address_id;

        const payloadObject = {
            cart_items: cartItems || [],
            billing_address_id: billingAddressId || null,
        };
        

        if (!cartItems || cartItems.length === 0) {
            setError('No items to checkout.');
            setLoading(false);
            return;
        }

        const fetchCheckoutDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await CartService.getCheckoutItems(payloadObject);

                if (response.success) {
                    const data = response.data;
                    setAssessmentItems(data.assessmentItems || []);
                    setCreditPackageItems(data.creditPackageItems || []);
                    setSubtotal(data.subtotal || 0);
                    setTotalDiscount(data.total_discount || 0);
                    setTotal(data.total || 0);
                    setBillingAddress(data.full_address || null);
                    setBillingAddressId(data.billing_address_id || null);

                    const firstItem = data.assessmentItems?.[0] || data.creditPackageItems?.[0];
                    if (firstItem?.currency_symbol) {
                        setCurrencySymbol(firstItem.currency_symbol);
                    }
                } else {
                    setError(response.message || 'Failed to load checkout details.');
                }
            } catch (err) {
                setError('Failed to load checkout details. Please try again.');
                console.error('Checkout fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchCheckoutDetails();
    }, [location.state]);

    const calculateExpirationDate = (expiryType, expiryDuration) => {
        if (!expiryType || !expiryDuration) return null;
        const END_OF_DAY_TIME = '18:30:00';

        if (expiryType === 'date') {
            return `${expiryDuration} ${END_OF_DAY_TIME}`;
        } else {
            const currentDateTime = new Date();
            const daysToAdd = parseInt(expiryDuration);
            const expiryDateTime = new Date(currentDateTime.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));

            const year = expiryDateTime.getFullYear();
            const month = String(expiryDateTime.getMonth() + 1).padStart(2, '0');
            const day = String(expiryDateTime.getDate()).padStart(2, '0');
            const hours = String(expiryDateTime.getHours()).padStart(2, '0');
            const minutes = String(expiryDateTime.getMinutes()).padStart(2, '0');
            const seconds = String(expiryDateTime.getSeconds()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
    };

    // Build cart payment object with assessment purchases and credit package purchases in a single payload.
    // and create the cart session for the total amount.

    const buildCartPaymentObject = () => {
        const session_id = sessionStorage.getItem('manage_user_id');
        const table_log_id = sessionStorage.getItem('table_log_id');
        const userType = sessionStorage.getItem('userType');
        const email = sessionStorage.getItem('email');
        const paid_by = sessionStorage.getItem('username');
        let enrollType = "user"
        let enrolledby = "";
        if (userType === "student") {
            enrolledby = "Student";
            enrollType = "student";
        } else if (userType === "admin") {
            enrolledby = "Admin";
        } else {
            enrolledby = "Parent";
        }

        let enrollment_create_user_id = "";
        let enrollment_create_user_table_id = "";
        if (userType === "student") {
            enrollment_create_user_id = creatorUserId || session_id;
            enrollment_create_user_table_id = creatorUserTableId || table_log_id;
        } else if (userType === "admin") {
            enrollment_create_user_id = session_id;
            enrollment_create_user_table_id = table_log_id;
        } else if (userType === "parent") {
            enrollment_create_user_id = sessionStorage.getItem("parent_group_id");
            enrollment_create_user_table_id = sessionStorage.getItem("parent_group_user_table_id");
        }

        // Common fields 
        const common = {
            email,
            cart_id : location.state?.cart_id || null,
            user_id: session_id,
            user_table_id: table_log_id,
            create_user_id: session_id,
            create_user_table_id: table_log_id,
            paid_by,
            userType,
            payment_method: 'Online',
            reference_id: 'N/A',
            form_list_id: 46,
            node_mid: 100,
            billing_address_id: billingAddressId || null,
        };

        // Assessment items 
        const assessment_items = assessmentItems.map((item) => ({
            cart_item_id: item.cart_item_id,
            assessment_id: item.assessment_id,
            attempt_no: item.attempt_no,
            assessment_name: item.name,
            quantity: item.quantity,
            user_id: userType === "student" ? session_id : item.user_id,
            user_table_id: 2,
            unit_amount: parseFloat(item.unit_price_after_discount).toFixed(2),
            unit_discount: parseFloat(item.unit_discount).toFixed(2),
            unit_price: parseFloat(item.unit_price).toFixed(2),
            line_subtotal: parseFloat(item.line_subtotal).toFixed(2),
            line_total: parseFloat(item.line_total).toFixed(2),
            line_discount: parseFloat(item.line_discount).toFixed(2),
            amount: parseFloat(item.line_total).toFixed(2),
            credits: parseInt(item.no_of_credits) || 0,
            currency_type: item.currency_symbol || currencySymbol,
            currency_type_id: item.currency_type_id || null,
            currencySymbol: item.currency_symbol || currencySymbol,
            currencyId: item.currency_type_id || null,
            currencyName: item.currency_name || null,
            enrolled_by: enrolledby,
            enrollment_create_user_id: enrollment_create_user_id,
            enrollment_create_user_table_id,
            enroll_user_id: userType === "student" ? session_id : item.user_id,
            enroll_user_table_id: 2,
        }));

        // Credit package items 
        const credit_package_items = creditPackageItems.map((item) => ({
            cart_item_id: item.cart_item_id,
            credit_package_id: item.credit_package_id,
            type_name: item.name,
            user_id: userType === "student" ? session_id : item.user_id,
            user_table_id: userType === "student" ? 2 : parseInt(table_log_id),
            credit_package_price_id: item.credit_package_price_id,
            quantity: item.quantity,
            credits: parseInt(item.credits) || 0,
            total_credits: parseInt(item.credits) || 0,
            available_credits: parseInt(item.credits) || 0,
            used_credits: 0,
            unit_amount: parseFloat(item.unit_price_after_discount).toFixed(2),
            unit_discount: parseFloat(item.unit_discount).toFixed(2),
            unit_price: parseFloat(item.unit_price).toFixed(2),
            line_subtotal: parseFloat(item.line_subtotal).toFixed(2),
            line_total: parseFloat(item.line_total).toFixed(2),
            line_discount: parseFloat(item.line_discount).toFixed(2),
            amount: parseFloat(item.line_total).toFixed(2),
            currency_type: item.level_currency_type || item.currency_symbol || currencySymbol,
            currency_type_id: parseInt(item.currency_id || item.currency_type_id) || null,
            expiration_date: calculateExpirationDate(item.credits_expiry_type, item.credits_expiry_duration),
            discount_rate: item.discount_rate || 0,
        }));

        return {
            ...common,
            total_amount: parseFloat(total).toFixed(2),
            subtotal: parseFloat(subtotal).toFixed(2),
            total_discount: parseFloat(totalDiscount).toFixed(2),
            currency_symbol: currencySymbol,
            assessment_items,
            credit_package_items,
            cart_items: location.state?.cart_items || [],
        };
    };

    // User accepts T&C, calls CombankService.getSessionIDCartPurchase to initiate the Mastercard payment gateway session.

    const handleConfirmPurchase = async () => {
        const result = await swal({
            title: "",
            text: "Are you sure you want to proceed with purchasing? Once you accept the Privacy Policy and Terms & Conditions, a secure third-party payment page will open to process your payment.",
            icon: "info",
            buttons: {
                cancel: "Cancel",
                confirm: {
                    text: "Proceed",
                    value: true,
                }
            },
            dangerMode: false
        });

        if (result) {
            const cart_payment_obj = buildCartPaymentObject();
            const pending = {
                cart_payment_obj,
                cart_items: location.state?.cart_items,
            };

            sessionStorage.setItem('pendingCartPurchase', JSON.stringify(pending));
            sessionStorage.setItem('creditPurches', 'true');

            history.push({
                pathname: routes.payment_terms_condition,
                state: { pendingCartPurchase: pending }
            });
        }
    };

    const handleBack = () => {
        history.goBack();
    };

    const hasItems = assessmentItems.length > 0 || creditPackageItems.length > 0;

    if (loading) {
        return (
            <div className="buy-cart-summary-page">
                <div className="buy-cart-summary-content">
                    <div className="buy-cart-summary-card">
                        <p style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                            Loading checkout details...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !hasItems) {
        return (
            <div className="buy-cart-summary-page">
                <div className="buy-cart-summary-content">
                    <div className="buy-cart-summary-empty-state">
                        <HiOutlineShoppingCart className="empty-state-icon" />
                        <h3 className="empty-state-title">No checkout items</h3>
                        <p className="empty-state-message">{error}</p>
                        <button onClick={handleBack} className="empty-state-button">
                            Back to Cart
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="buy-cart-summary-page">
            {/* Header Section */}
            <div className="buy-cart-summary-header">
                <div className="buy-cart-summary-header-left">
                    <HiShoppingCart className="buy-cart-summary-header-icon" />
                    <h1 className="buy-cart-summary-title">Order Summary</h1>
                </div>
                <div className="buy-cart-summary-header-right">
                    <button
                        className="buy-cart-summary-back-btn"
                        onClick={handleBack}
                    >
                        Back
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="buy-cart-summary-content">
                <div className="buy-cart-summary-card">
                    {error && (
                        <div style={{ padding: '12px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '16px' }}>
                            {error}
                        </div>
                    )}

                    {/* Billing Address Section */}
                    {billingAddress && (
                        <div className="buy-cart-summary-section" style={{ backgroundColor: '#f0f9ff', padding: '16px', borderRadius: '8px', marginBottom: '16px', borderLeft: '4px solid #a741cf' }}>
                            <h3 className="buy-cart-summary-section-title" style={{ marginTop: 0, marginBottom: '8px' }}>Billing Address</h3>
                            <p style={{ margin: '0', color: '#070707', lineHeight: '1.5' }}>
                                {billingAddress}
                            </p>
                        </div>
                    )}

                    {/* Assessments Section */}
                    {assessmentItems.length > 0 && (
                        <div className="buy-cart-summary-section">
                            <h2 className="buy-cart-summary-section-title">Assessments</h2>
                            <div className="buy-cart-summary-items">
                                {assessmentItems.map((item) => (
                                    <div key={item.cart_item_id} className="buy-cart-summary-item">
                                        <div className="buy-cart-summary-item-left">
                                            <HiDocumentText className="buy-cart-summary-item-icon green-icon" />
                                            <div>
                                                <span className="buy-cart-summary-item-name">
                                                    {item.name}
                                                </span>
                                                <span>
                                                    {item.user_code && ` For Student ${item.user_code}`}
                                                </span>
                                                <span className="buy-cart-summary-item-qty">
                                                    Qty: {item.quantity} × {currencySymbol} {item.unit_price_after_discount.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        <span className="buy-cart-summary-item-price">
                                            {currencySymbol} {item.line_total.toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Credit Packages Section */}
                    {creditPackageItems.length > 0 && (
                        <div className="buy-cart-summary-section">
                            <h2 className="buy-cart-summary-section-title">Credit Packages</h2>
                            <div className="buy-cart-summary-items">
                                {creditPackageItems.map((item) => (
                                    <div key={item.cart_item_id} className="buy-cart-summary-item">
                                        <div className="buy-cart-summary-item-left">
                                            <HiCircleStack className="buy-cart-summary-item-icon pink-icon" />
                                            <div>
                                                <span className="buy-cart-summary-item-name">
                                                    {item.name} ({item.credits} Credits)                                                    
                                                    {item.discount_rate > 0 && ` — ${item.discount_rate}% off`}
                                                </span>
                                                <span>
                                                    {item.user_code && ` For Student ${item.user_code}`}
                                                </span>
                                                <span className="buy-cart-summary-item-qty">
                                                    Qty: {item.quantity} × {currencySymbol} {item.unit_price_after_discount.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        <span className="buy-cart-summary-item-price">
                                            {currencySymbol} {item.line_total.toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Subtotal & Discount */}
                    {hasItems && (
                        <>
                            <div className="buy-cart-summary-discount">
                                <span className="buy-cart-summary-discount-label">Subtotal</span>
                                <span className="buy-cart-summary-discount-amount">
                                    {currencySymbol} {subtotal.toFixed(2)}
                                </span>
                            </div>

                            {totalDiscount > 0 && (
                                <div className="buy-cart-summary-discount">
                                    <span className="buy-cart-summary-discount-label">Discount</span>
                                    <span className="buy-cart-summary-discount-amount" style={{ color: '#16a34a' }}>
                                        − {currencySymbol} {totalDiscount.toFixed(2)}
                                    </span>
                                </div>
                            )}

                            {/* Total */}
                            <div className="buy-cart-summary-total">
                                <span className="buy-cart-summary-total-label">Total Amount</span>
                                <span className="buy-cart-summary-total-amount">
                                    {currencySymbol} {total.toFixed(2)}
                                </span>
                            </div>

                            {/* Confirm Button */}
                            <button
                                className="buy-cart-summary-confirm-btn"
                                onClick={handleConfirmPurchase}
                            >
                                <HiShoppingCart className="buy-cart-summary-confirm-icon" />
                                Confirm Purchase
                            </button>

                            {/* Payment Gateway Image */}
                            <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                <img src={PaymentGateSolutions} className="paymentgateimg" alt="Payment Gateway Solutions" />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BuyCartSummary;
